import React from 'react';
import styled from 'styled-components';
import {
  heading4FontSize,
  body1LineHeight,
  screenSmMin,
  spacer8,
  spacer12,
  spacer20,
  spacer24,
  spacer32,
  spacer40,
  spacer80,
} from '@spotify-internal/encore-foundation';
import { Type } from '@spotify-internal/encore-web';
import { ContentfulAsset } from '../../../Contentful';
import { ContentfulImage } from '../../../Contentful/ContentfulImage';
import { Box, ImageAspectBox1x1 } from '../../../ui';
import { ModuleType } from '../../index';
import { Colors } from './colorsType';

const Wrapper = styled(Box)`
  background-color: ${(props: Colors) => props.backgroundColor};
  color: ${(props: Colors) => props.textColor};
  height: auto;

  &.tallest {
    height: 100%;
  }
`;

const HeroDescriptionWrapper = styled.div`
  padding-top: ${spacer20};
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: 'flex-start';
  @media (max-width: ${screenSmMin}) {
    padding-top: 0px;
  }
`;

const HeroDescriptionWrapperNoImage = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: ${(props: { centered: boolean }) =>
    props.centered ? 'center' : 'flex-start'};
`;

const StyledText = styled.div`
  width: 55%;
  padding-bottom: 0;
  text-align: left;
  padding-left: ${spacer80};

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    padding-left: 0px;
    padding-top: ${spacer8};
  }
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
`;

// TODO Remove hardcoding of font-size and line-height for mobile
const DescriptionWrapper = styled(Type.p).attrs({
  variant: Type.heading2,
  weight: Type.book,
})`
  padding-bottom: 0;
  @media (max-width: ${screenSmMin}) {
    width: 100%;
    padding-left: 0;
    font-size: ${heading4FontSize};
    line-height: ${body1LineHeight};
  }
`;
const DescriptionWrapperNoImage = styled(Type.p).attrs({
  variant: Type.heading2,
  weight: Type.book,
})`
  padding-bottom: 0;
  text-align: center;
  align-self: center;
  max-width: 867px;
  @media (max-width: ${screenSmMin}) {
    width: 100%;
    padding-left: 0;
  }

  @media (max-width: ${screenSmMin}) {
    font-size: ${spacer24};
    line-height: ${spacer32};
  }
`;

const MaxWidth = styled.div`
  max-width: 867px;
  ${(props: { centered: boolean }) =>
    props.centered
      ? 'align-self: center; display: flex; flex-direction: column;'
      : ''};
`;

const ImageWrapper = styled(ImageAspectBox1x1)`
  width: 30%;
  display: flex;
  align-items: center;
  height: fit-content;
  align-self: center;

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    margin-bottom: ${spacer8};
  }
`;

const StyledSupertext = styled(Type).attrs({
  variant: Type.body1,
  weight: Type.bold,
})`
  padding-bottom: ${spacer20};
  text-transform: uppercase;
  @media (max-width: ${screenSmMin}) {
    padding-left: 0;
    padding: ${spacer12} 0px;
    width: 100%;
  }
`;
const StyledSupertextNoImage = styled(Type).attrs({
  variant: Type.body1,
  weight: Type.bold,
})`
  padding: 0;
  height: auto;
  align-self: center;
  text-align: center;
  text-transform: uppercase;
  padding-bottom: ${spacer40};
  @media (max-width: ${screenSmMin}) {
    padding-left: 0;
    padding-top: ${spacer8};
    width: 100%;
  }
`;

const BodyTextContainer = styled.div`
  height: 100%;
`;

type HeroDescriptionType = {
  supertext: string;
  body: string;
  image?: ContentfulAsset;
  backgroundColor: string;
  textColor: string;
};

export const HeroDescription = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeroDescriptionType
>(
  (
    { supertext = '', body = '', image, backgroundColor = '', textColor = '' },
    ref,
  ) => {
    const centered = image === undefined;
    return !centered ? (
      <Wrapper
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <HeroDescriptionWrapper>
          {image && (
            <ImageWrapper>
              <ContentfulImage asset={image} />
            </ImageWrapper>
          )}
          <StyledText>
            <BodyTextContainer>
              <StyledBody>
                <StyledSupertext>{supertext}</StyledSupertext>
                <MaxWidth centered={centered}>
                  <DescriptionWrapper>{body}</DescriptionWrapper>
                </MaxWidth>
              </StyledBody>
            </BodyTextContainer>
          </StyledText>
        </HeroDescriptionWrapper>
      </Wrapper>
    ) : (
      <Wrapper
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <HeroDescriptionWrapperNoImage centered={centered}>
          <MaxWidth centered={centered}>
            <StyledSupertextNoImage>{supertext}</StyledSupertextNoImage>
            <DescriptionWrapperNoImage>{body}</DescriptionWrapperNoImage>
          </MaxWidth>
        </HeroDescriptionWrapperNoImage>
      </Wrapper>
    );
  },
);
